import { useState, useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const symbolMap = {
  // Математический анализ
  '∫': '\\int_{a}^{b} f(x)dx = F(b) - F(a)',
  '∑': '\\sum_{n=1}^{\\infty} a_n = \\lim_{N \\to \\infty} S_N',
  '∞': '\\lim_{x \\to a} f(x) = L',
  '∂': '\\frac{\\partial f}{\\partial x} = \\lim_{h \\to 0} \\frac{f(x+h)-f(x)}{h}',
  '∇': '\\nabla \\cdot \\mathbf{F} = \\rho',
  
  // Комплексные числа
  'ℂ': 'z = a + bi \\in \\mathbb{C}',
  'ℜ': '\\Re(z) = a',
  'ℑ': '\\Im(z) = b',
  '∗': 'z \\cdot \\overline{z} = a^2 + b^2',
  'ⅈ': 'i^2 = -1',
  
  // Алгебра
  '≡': 'a \\equiv b \\ (\\text{mod} \\ n)',
  '√': '\\sqrt[n]{x} = x^{1/n}',
  '∆': '\\Delta = b^2 - 4ac',
  '±': 'x = \\frac{-b \\pm \\sqrt{\\Delta}}{2a}',
  'Π': '\\prod_{k=1}^n k = n!',
  
  // Геометрия
  '∠': '\\triangle ABC \\sim \\triangle A\'B\'C\'',
  '∢': '\\alpha + \\beta + \\gamma = 180^\\circ',
  '∼': '\\frac{a}{\\sin A} = \\frac{b}{\\sin B} = 2R',
  '∥': '\\vec{a} \\times \\vec{b} = |a||b|\\sin\\theta',
  
  // Теория вероятностей
  'σ': '\\sigma = \\sqrt{\\frac{1}{N}\\sum_{i=1}^N (x_i - \\mu)^2}',
  'μ': '\\mu = E[X] = \\sum x_i p_i',
  'ρ': '\\rho_{X,Y} = \\frac{\\text{cov}(X,Y)}{\\sigma_X \\sigma_Y}',
  'λ': 'P(X=k) = \\frac{\\lambda^k e^{-\\lambda}}{k!}',
  
  // Топология
  '∈': 'x \\in \\partial A \\iff \\forall \\epsilon > 0 \\ B_\\epsilon(x) \\cap A \\neq \\emptyset',
  '∀': '\\forall \\epsilon > 0 \\ \\exists \\delta > 0: |x-a| < \\delta \\Rightarrow |f(x)-L| < \\epsilon',
  '∃': '\\exists x \\in \\mathbb{R}: x^2 = 2',
  '∅': 'A \\cap B = \\emptyset',
  
  // Теория множеств
  '∪': 'A \\cup B = \\{x: x \\in A \\lor x \\in B\\}',
  '∩': 'A \\cap B = \\{x: x \\in A \\land x \\in B\\}',
  '⊂': 'A \\subset B \\iff \\forall x \\in A \\Rightarrow x \\in B',
  'ℵ': '\\aleph_0 + \\aleph_0 = \\aleph_0',
  
  // Физика
  'E': 'mc^2',
  'Ψ': 'i\\hbar\\frac{\\partial}{\\partial t} \\Psi = \\hat{H}\\Psi',
  'ℏ': 'E = \\hbar\\omega',
  'θ': '\\tau = rF\\sin\\theta',
  'Φ': '\\Phi_B = \\oint \\mathbf{B} \\cdot d\\mathbf{A}',
  
  // Универсальные формулы
  'Ξ': 'e^{i\\pi} + 1 = 0', // Формула Эйлера
  'Γ': '\\Gamma(z) = \\int_0^\\infty t^{z-1}e^{-t}dt', // Гамма-функция
  'Σ': '\\frac{1}{\\zeta(s)} = \\prod_{p \\ \\text{prime}} \\left(1 - \\frac{1}{p^s}\\right)', // Дзета-функция
  'Δ': '\\Delta x \\Delta p \\geq \\frac{\\hbar}{2}', // Принцип неопределенности
  'Ω': 'S = k_B \\ln \\Omega', // Энтропия Больцмана
  'Λ': '\\Lambda CDM \\text{ model: } \\Omega_m + \\Omega_\\Lambda = 1', // Космология
  '∮': '\\oint_{\\partial S} \\mathbf{E} \\cdot d\\mathbf{l} = -\\frac{d\\Phi_B}{dt}', // Закон Фарадея
  '⊗': '\\mathbf{A} \\otimes \\mathbf{B} = [a_{ij}\\mathbf{B}]', // Тензорное произведение
  '⊕': 'V \\oplus W = \\{v + w | v \\in V, w \\in W\\}', // Прямая сумма
  '⇒': '\\text{If } A \\subset B \\text{ and } B \\subset A \\Rightarrow A = B', // Логическая импликация
  '⇔': 'x \\in \\mathbb{R} \\Leftrightarrow x^2 \\geq 0', // Логическая эквивалентность
  '∴': 'A \\Rightarrow B, \\ A \\ \\therefore \\ B', // Знак "следовательно"
  '∵': 'B \\ \\because \\ A \\Rightarrow B', // Знак "поскольку"
  '≅': 'G \\cong H \\iff \\exists \\phi: G \\to H \\text{ (isomorphism)}',
  '≈': '\\sin x \\approx x - \\frac{x^3}{6} \\text{ at } x \\to 0',
  '≠': 'a \\neq b \\iff \\exists \\epsilon > 0: |a - b| > \\epsilon',
  '≡': 'f(x) \\equiv g(x) \\text{ on } \\mathbb{R}'
};


const FloatingSymbol = ({ 
  symbol, 
  explosionPhase, 
  onSymbolClick, 
  gravityCenter,
  animationSettings = { // Добавляем значения по умолчанию
    collapseDuration: 2000,
    explodePower: 15,
    enableDeceleration: true,
    decelerationDuration: 2000
  } 
}) => {
  const [showFormula, setShowFormula] = useState(false);
  const symbolRef = useRef(null);
  const animationFrame = useRef(null);
  
  // Сохраняем оригинальные скорости
  const originalSpeed = useRef({
    dx: (Math.random() - 0.5) * 1.2,
    dy: (Math.random() - 0.5) * 1.2
  });

  // Параметры анимации
  const animationParams = useRef({
    startTime: null,
    startDx: 0,
    startDy: 0,
    isDecelerating: false
  });

  const pos = useRef({
    x: Math.random() * window.innerWidth,
    y: Math.random() * window.innerHeight,
    dx: originalSpeed.current.dx,
    dy: originalSpeed.current.dy,
    angle: Math.random() * 360
  });

    useEffect(() => {
    if (explosionPhase === 'explode') {
      const angle = Math.random() * Math.PI * 2;
      const speed = animationSettings.explodePower + Math.random() * 5;
      
      pos.current.dx = Math.cos(angle) * speed;
      pos.current.dy = Math.sin(angle) * speed;

      if (animationSettings.enableDeceleration) {
        animationParams.current = {
          startTime: Date.now(),
          startDx: pos.current.dx,
          startDy: pos.current.dy,
          isDecelerating: true
        };
        
        setTimeout(() => {
          animationParams.current.isDecelerating = false;
        }, animationSettings.decelerationDuration);
      }
    }
  }, [explosionPhase, animationSettings]);

  const updatePosition = () => {
    if (!symbolRef.current) return;

    // Логика замедления
    if (animationParams.current.isDecelerating && animationSettings.enableDeceleration) {
      const progress = Math.min(1, 
        (Date.now() - animationParams.current.startTime) / 
        animationSettings.decelerationDuration
      );
      
      pos.current.dx = lerp(
        animationParams.current.startDx,
        originalSpeed.current.dx,
        progress
      );
      
      pos.current.dy = lerp(
        animationParams.current.startDy,
        originalSpeed.current.dy,
        progress
      );
    }

    // Обновление позиции
    pos.current.x += pos.current.dx;
    pos.current.y += pos.current.dy;

    if (explosionPhase === 'collapse') {
      // Тянем к центру плашки (или экрана), выравнивая по центру самого символа
      const centerX = gravityCenter ? gravityCenter.x : window.innerWidth / 2;
      const centerY = gravityCenter ? gravityCenter.y : window.innerHeight / 2;
      const directionX = centerX - (pos.current.x + symbolRef.current.offsetWidth / 2);
      const directionY = centerY - (pos.current.y + symbolRef.current.offsetHeight / 2);
      const distance = Math.sqrt(directionX ** 2 + directionY ** 2);
      const speed = 3; // Скорость движения в пикселях в секунду
      
      if (distance > 0) {
        pos.current.dx = (directionX / distance) * speed;
        pos.current.dy = (directionY / distance) * speed;
      }
    }

    pos.current.angle += 0.3;

    const boundary = 50;
    if (explosionPhase === null) {
      if (pos.current.x > window.innerWidth + boundary) pos.current.x = -boundary;
      if (pos.current.x < -boundary) pos.current.x = window.innerWidth + boundary;
      if (pos.current.y > window.innerHeight + boundary) pos.current.y = -boundary;
      if (pos.current.y < -boundary) pos.current.y = window.innerHeight + boundary;
    }

    symbolRef.current.style.transform = `
      translate(${pos.current.x}px, ${pos.current.y}px)
      rotate(${pos.current.angle}deg)
    `;

    animationFrame.current = requestAnimationFrame(updatePosition);
  };

  // Функция линейной интерполяции
  const lerp = (start, end, progress) => {
    return start + (end - start) * progress;
  };

  useEffect(() => {
    animationFrame.current = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(animationFrame.current);
  }, [explosionPhase]);

  // Обработчик клика
  const handleClick = () => {
    onSymbolClick(symbol);
  };

  // Генерация формулы
  const formulaHTML = useRef('');
  useEffect(() => {
    try {
      formulaHTML.current = katex.renderToString(`= ${symbolMap[symbol]}`, {
        throwOnError: true,
        displayMode: false
      });
    } catch (e) {
      console.error('KaTeX error:', e);
    }
  }, [symbol]);

  return (
    <div
      ref={symbolRef}
      style={{
        position: 'fixed',
        willChange: 'transform',
        cursor: 'pointer',
        fontSize: window.innerWidth < 600 ? '24px' : '32px',
        zIndex: symbol === 'E' ? 2 : 1,
      }}
      onClick={handleClick}
    >
      <div 
        style={{
          position: 'relative',
          display: 'inline-block',
          padding: '20px',
          transition: 'transform 0.3s',
          transform: explosionPhase ? 'scale(1.5)' : 'scale(1)',
        }}
        onMouseEnter={() => setShowFormula(true)}
        onMouseLeave={() => setShowFormula(false)}
      >
        <div style={{ 
          display: 'inline-block',
          transition: 'transform 0.2s',
          transform: showFormula ? 'scale(1.2)' : 'scale(1)'
        }}>
          {symbol}
        </div>
        {showFormula && (
          <div
            style={{ 
              position: 'absolute',
              left: '100%',
              top: '50%',
              transform: 'translateY(-50%)',
              paddingLeft: '15px',
              backgroundColor: 'rgba(255,255,255,0.95)',
              borderRadius: '4px',
              whiteSpace: 'nowrap',
            }}
            dangerouslySetInnerHTML={{ __html: formulaHTML.current }}
          />
        )}
      </div>
    </div>
  );
};

const MathBackground = ({ getGravityCenter }) => {
  // E (пасхалка) — первым, чтобы он был на экране при любом числе символов
  const symbols = ['E', ...Object.keys(symbolMap).filter(s => s !== 'E')];
  // Число символов зависит от площади экрана: 100 на Full HD, на телефоне ~30
  const symbolCount = Math.round(
    Math.min(100, Math.max(30, (window.innerWidth * window.innerHeight) / (1920 * 1080) * 100))
  );
  const [explosionPhase, setExplosionPhase] = useState(null);
  const [gravityCenter, setGravityCenter] = useState(null);

  // Полностью настраиваемые параметры
  const animationSettings = {
    collapseDuration: 6000,    // Время схлопывания к центру
    explodePower: 10,          // Сила взрыва (скорость разлета)
    enableDeceleration: true,  // Включить плавное замедление
    decelerationDuration: 5000 // Время замедления до исходной скорости
  };

  const handleSymbolClick = (clickedSymbol) => {
    if (clickedSymbol === 'E') {
      // Центр гравитации задаёт страница (иначе — центр экрана)
      setGravityCenter(getGravityCenter ? getGravityCenter() : null);
      setExplosionPhase('collapse');
      
      setTimeout(() => {
        setExplosionPhase('explode');
        
        // Автоматический сброс через заданное время
        setTimeout(() => {
          setExplosionPhase(null);
        }, animationSettings.decelerationDuration + 500);
      }, animationSettings.collapseDuration);
    }
  };

  return (
    // translate="no" — чтобы переводчик браузера не «переводил» формулы
    <div translate="no" className="notranslate" style={{
      position: 'fixed',
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
      backgroundColor: 'white'
    }}>
      {Array.from({ length: symbolCount }).map((_, i) => (
        <FloatingSymbol 
          key={i}
          symbol={symbols[i % symbols.length]}
          explosionPhase={explosionPhase}
          onSymbolClick={handleSymbolClick}
          gravityCenter={gravityCenter}
          animationSettings={animationSettings} // Добавить эту строку
        />
      ))}
    </div>
  );
};

export default MathBackground;